import { useState, useEffect } from "react";
import axios, { API } from "../../api";

const TIER_LABEL = {
  spark: "Spark",
  efflora: "Efflora",
  aurora: "Aurora",
  eternal: "Eternal",
};

/**
 * Payment / upgrade modal.
 * - tier === 'base'  → simple one-time $4.99 (handles referral 25% discount)
 * - tier === 'premium' (or any of spark/efflora/aurora/eternal) → tier picker UI
 *   where the user selects which monthly plan to subscribe to.
 */
export function PaymentModal({ tier, user = null, onClose, onSuccess: _onSuccess, discountPct = 0, currentTier = null, onOpenPromo = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tiers, setTiers] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(currentTier || "spark");
  const [loadingTiers, setLoadingTiers] = useState(false);
  // Monthly vs Yearly toggle — yearly saves ~17% (2 months free). Default monthly.
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // Bundle state (only meaningful on Base flow for users who haven't bought yet)
  const [bundleInfo, setBundleInfo] = useState(null);
  const [bundleMode, setBundleMode] = useState(false); // false = plain Base, true = bundle
  const [bundleTier, setBundleTier] = useState("efflora");
  const [bundleAgreed, setBundleAgreed] = useState(true); // default-checked per product spec

  const isBaseFlow = tier === "base";
  const showDiscount = isBaseFlow && discountPct > 0 && discountPct < 100;
  const discountedPrice = showDiscount ? (4.99 * (100 - discountPct) / 100).toFixed(2) : null;

  useEffect(() => {
    if (isBaseFlow) return;
    setLoadingTiers(true);
    axios.get(`${API}/tiers`).then((res) => {
      setTiers(res.data.tiers || []);
      setLoadingTiers(false);
    }).catch(() => setLoadingTiers(false));
  }, [isBaseFlow]);

  // Fetch bundle info on Base flow — drives "Founding Member" vs "Starter" framing
  useEffect(() => {
    if (!isBaseFlow) return;
    axios.get(`${API}/bundle/info`).then((res) => {
      setBundleInfo(res.data);
      if (res.data?.default_tier) setBundleTier(res.data.default_tier);
    }).catch(() => { /* bundle is a nice-to-have; failing silently is fine */ });
  }, [isBaseFlow]);

  const handleBasePayment = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payment/create-checkout`, {
        tier: "base",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start payment");
      setLoading(false);
    }
  };

  const handleBundlePayment = async () => {
    if (!bundleAgreed) {
      setError("Please confirm the subscription agreement to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payment/create-checkout`, {
        tier: "base",  // legacy required field; bundle_tier is the real signal
        bundle_tier: bundleTier,
        bundle_agreed: bundleAgreed,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start payment");
      setLoading(false);
    }
  };

  const handleTierPayment = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payment/create-checkout`, {
        tier: selectedSlug,
        origin_url: window.location.origin,
        billing_period: billingPeriod,
      });
      // Phase 19.7 — if user already has an active sub, backend prorated and
      // modified it in place. No Stripe redirect needed.
      if (res.data.mode === "subscription_modified") {
        const verb = res.data.is_upgrade ? "Upgraded" : "Switched";
        if (typeof onClose === "function") onClose();
        // Soft reload so /api/me reflects the new tier everywhere
        setTimeout(() => {
          try { window.toast?.(res.data.message || `${verb} to ${selectedSlug}!`); } catch (e) {}
          window.location.reload();
        }, 250);
        return;
      }
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start payment");
      setLoading(false);
    }
  };

  // ---------- BASE flow ----------
  if (isBaseFlow) {
    const isFounding = bundleInfo?.founding?.active;
    const bundlePricing = bundleInfo?.pricing?.[bundleTier];
    const bundleLabel = isFounding ? "Founding Member Bundle" : "Starter Bundle";
    const bundleEmoji = isFounding ? "🏆" : "✨";

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="payment-modal" onClick={(e) => e.stopPropagation()} data-testid="payment-modal-base">
          <div className="modal-icon">✨</div>
          <h2>{bundleMode ? bundleLabel : "Continue with Aura"}</h2>
          <p className="modal-subtitle">{showDiscount ? "Your friend's gift" : (bundleMode ? "Base + your first month of premium, together" : "Your trial has ended")}</p>

          {/* Phase H — "Aura lives on your phone" marketing badge.
              Surfaced here because this is the highest-intent screen during
              the trial-conversion moment. Honest, not aspirational — the
              on-device LLM ("Aura's brain") + on-device image gen ("Aura's
              brush") are both real and shipping. */}
          {!showDiscount && !bundleMode && (
            <div className="on-device-badge" data-testid="on-device-privacy-badge">
              <div className="on-device-badge-icon">✦</div>
              <div className="on-device-badge-copy">
                <strong>Aura lives on your phone.</strong>
                <span>
                  Her brain &amp; her brush run on-device. Your conversations and the wallpapers she paints for you never leave your phone unless you ask.
                </span>
              </div>
            </div>
          )}

          {/* Bundle promo card — shown above the plain Base option when bundle data is loaded */}
          {bundleInfo && !showDiscount && !bundleMode && (
            <div className="bundle-promo-card" data-testid="bundle-promo-card" onClick={() => setBundleMode(true)}>
              <div className="bundle-promo-header">
                <span className="bundle-promo-emoji">{bundleEmoji}</span>
                <div className="bundle-promo-titles">
                  <strong>{bundleLabel}</strong>
                  <span>Add premium with <strong>20% off</strong> your first month</span>
                </div>
                {isFounding && (
                  <span className="bundle-founding-pill" data-testid="bundle-founding-pill">
                    {bundleInfo.founding.remaining.toLocaleString()} spots left
                  </span>
                )}
              </div>
              <button type="button" className="bundle-promo-cta" data-testid="bundle-promo-cta">
                See the bundle →
              </button>
            </div>
          )}

          {bundleMode ? (
            <>
              {/* Tier picker inside the bundle */}
              <div className="bundle-tier-picker" data-testid="bundle-tier-picker">
                <label>Choose your premium tier:</label>
                <div className="bundle-tier-row">
                  <img
                    src={`/tiers/${bundleTier}.png`}
                    alt=""
                    className="bundle-tier-icon"
                    data-testid={`bundle-tier-icon-${bundleTier}`}
                  />
                  <select
                    value={bundleTier}
                    onChange={(e) => setBundleTier(e.target.value)}
                    data-testid="bundle-tier-select"
                    disabled={isFounding}
                  >
                    <option value="spark">Spark — $1.99/mo</option>
                    <option value="efflora">Efflora — $4.99/mo (most popular)</option>
                    <option value="aurora">Aurora — $9.99/mo</option>
                    <option value="eternal">Eternal — $19.99/mo</option>
                  </select>
                </div>
                {isFounding && (
                  <small className="bundle-tier-locked-note">
                    Founding members get Efflora — the middle-of-the-road tier with the best preview of the higher plans.
                  </small>
                )}
              </div>

              {/* Price breakdown */}
              {bundlePricing && (
                <div className="bundle-price-breakdown" data-testid="bundle-price-breakdown">
                  <div className="bundle-line">
                    <span>Aura Base (lifetime)</span>
                    <span>${bundlePricing.base_price.toFixed(2)}</span>
                  </div>
                  <div className="bundle-line">
                    <span>{TIER_LABEL[bundleTier]} — first month <small>(20% off)</small></span>
                    <span>
                      <s className="bundle-strike">${bundlePricing.tier_full_price.toFixed(2)}</s>
                      &nbsp;${bundlePricing.tier_discounted_price.toFixed(2)}
                    </span>
                  </div>
                  <div className="bundle-line bundle-line-total">
                    <span>Total today</span>
                    <span>${bundlePricing.total.toFixed(2)}</span>
                  </div>
                  <div className="bundle-savings-line">
                    You save ${bundlePricing.savings.toFixed(2)} this month
                  </div>
                </div>
              )}

              {/* Default-checked agreement */}
              <label className="bundle-agree-row" data-testid="bundle-agree-row">
                <input
                  type="checkbox"
                  checked={bundleAgreed}
                  onChange={(e) => setBundleAgreed(e.target.checked)}
                  data-testid="bundle-agree-checkbox"
                />
                <span>
                  I understand my <strong>{TIER_LABEL[bundleTier]}</strong> plan continues at the regular monthly rate after the first month. I can cancel anytime in Settings.
                </span>
              </label>

              {error && <div className="error-message">{error}</div>}

              <button
                className="init-btn"
                onClick={handleBundlePayment}
                disabled={loading || !bundleAgreed || !bundlePricing}
                data-testid="pay-bundle-btn"
              >
                {loading ? "Loading..." : `Get the bundle — $${bundlePricing ? bundlePricing.total.toFixed(2) : "…"}`}
              </button>

              <button
                type="button"
                className="promo-link-inline"
                onClick={() => setBundleMode(false)}
                data-testid="bundle-back-link"
              >
                ← Just the Base ($4.99)
              </button>
            </>
          ) : (
            <>
              <ul className="features-list">
                <li>✦ AI companion conversations</li>
                <li>✦ Dynamic mood-based wallpapers</li>
                <li>✦ Lifetime access</li>
                <li>✦ No recurring charges</li>
              </ul>
              {showDiscount ? (
                <>
                  <div className="referral-discount-badge" data-testid="referral-discount-badge">
                    🎁 {discountPct}% off via referral
                  </div>
                  <div className="price-display">
                    <span className="price-old">$4.99</span>
                    <span className="price">${discountedPrice}</span>
                    <span className="period">one-time</span>
                  </div>
                </>
              ) : (
                <div className="price-display">
                  <span className="price">$4.99</span>
                  <span className="period">one-time</span>
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              <button
                className="init-btn"
                onClick={handleBasePayment}
                disabled={loading}
                data-testid="pay-base-btn"
              >
                {loading ? "Loading..." : `Continue for $${showDiscount ? discountedPrice : "4.99"}`}
              </button>
              {onOpenPromo && (
                <button
                  type="button"
                  className="promo-link-inline"
                  onClick={onOpenPromo}
                  data-testid="payment-promo-link"
                >
                  Have a promo code?
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ---------- TIER PICKER (premium) ----------
  return (
    <div className="modal-overlay tier-picker-overlay" onClick={onClose}>
      <div className="payment-modal tier-picker-modal" onClick={(e) => e.stopPropagation()} data-testid="payment-modal-premium">
        <div className="modal-icon">💜</div>
        <h2>Choose your Aura</h2>
        <p className="modal-subtitle">Start with a coffee&apos;s worth. Upgrade anytime.</p>

        {/* Monthly / Yearly toggle — yearly saves ~17% */}
        <div className="billing-period-toggle" role="tablist" data-testid="billing-period-toggle">
          <button
            type="button"
            role="tab"
            aria-selected={billingPeriod === "monthly"}
            className={`billing-period-btn ${billingPeriod === "monthly" ? "active" : ""}`}
            onClick={() => setBillingPeriod("monthly")}
            data-testid="billing-period-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billingPeriod === "yearly"}
            className={`billing-period-btn ${billingPeriod === "yearly" ? "active" : ""}`}
            onClick={() => setBillingPeriod("yearly")}
            data-testid="billing-period-yearly"
          >
            Yearly <span className="billing-period-save">Save ~17%</span>
          </button>
        </div>

        {loadingTiers ? (
          <div className="tier-cards-loading">Loading plans…</div>
        ) : (
          <div className="tier-cards" data-testid="tier-cards">
            {tiers.map((t) => {
              const selected = selectedSlug === t.slug;
              const isCurrent = currentTier === t.slug;
              return (
                <button
                  key={t.slug}
                  type="button"
                  className={`tier-card ${selected ? "selected" : ""} ${isCurrent ? "current" : ""}`}
                  onClick={() => setSelectedSlug(t.slug)}
                  data-testid={`tier-card-${t.slug}`}
                  style={{ "--tier-color": t.color }}
                >
                  <div className="tier-card-header">
                    <img
                      src={`/tiers/${t.slug}.png`}
                      alt=""
                      className="tier-icon"
                      data-testid={`tier-icon-${t.slug}`}
                    />
                    <span className="tier-name">{t.name}</span>
                    {isCurrent && <span className="tier-current-badge">Current</span>}
                    {t.slug === "eternal" && !isCurrent && (
                      <span className="tier-best-value-badge" data-testid="tier-best-value-badge">
                        ⭐ Best value
                      </span>
                    )}
                  </div>
                  <div className="tier-price">
                    {billingPeriod === "yearly" && t.price_usd_yearly ? (
                      <>
                        {user?.has_byok_active && t.byok_price_usd_yearly ? (
                          <>
                            <span className="tier-price-strike">${t.price_usd_yearly}</span>
                            <span className="tier-price-main">${t.byok_price_usd_yearly}</span>
                          </>
                        ) : (
                          <span className="tier-price-main">${t.price_usd_yearly}</span>
                        )}
                        <span className="tier-price-period">/yr</span>
                      </>
                    ) : (
                      <>
                        {user?.has_byok_active && t.byok_price_usd ? (
                          <>
                            <span className="tier-price-strike">${t.price_usd}</span>
                            <span className="tier-price-main">${t.byok_price_usd}</span>
                          </>
                        ) : (
                          <span className="tier-price-main">${t.price_usd}</span>
                        )}
                        <span className="tier-price-period">/mo</span>
                      </>
                    )}
                    {user?.has_byok_active && (
                      <span className="tier-byok-badge" data-testid={`tier-byok-badge-${t.slug}`}>
                        ✦ BYOK 50% OFF
                      </span>
                    )}
                  </div>
                  {billingPeriod === "yearly" && t.yearly_savings_usd > 0 && (
                    <div className="tier-yearly-savings" data-testid={`tier-yearly-savings-${t.slug}`}>
                      Saves ${t.yearly_savings_usd.toFixed(2)}/yr
                    </div>
                  )}
                  {billingPeriod === "monthly" && t.slug === "eternal" && (
                    <div className="tier-annual-hint" data-testid="tier-annual-hint">
                      That&apos;s <strong>${(Number(t.price_usd) * 12).toFixed(0)}/yr</strong>
                      &nbsp;— or <strong>${t.price_usd_yearly}/yr</strong> if billed annually.
                      Saves <strong>~$190/yr</strong> vs picking every add-on à la carte (~$41/mo).
                    </div>
                  )}
                  <div className="tier-tagline">{t.tagline}</div>
                  <ul className="tier-features">
                    {tierBullets(t).map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <button
          className="init-btn"
          onClick={handleTierPayment}
          disabled={loading || loadingTiers}
          data-testid={`pay-${selectedSlug}-btn`}
        >
          {loading
            ? "Opening checkout…"
            : billingPeriod === "yearly"
              ? `Start ${TIER_LABEL[selectedSlug] || "Premium"} — $${YEARLY_PRICE_BY_SLUG[selectedSlug]}/yr`
              : `Start ${TIER_LABEL[selectedSlug] || "Premium"} — $${PRICE_BY_SLUG[selectedSlug]}/mo`}
        </button>
        <button className="ghost-btn small" onClick={onClose} data-testid="close-payment-btn">
          Maybe later
        </button>
        {onOpenPromo && (
          <button
            type="button"
            className="promo-link-inline"
            onClick={onOpenPromo}
            data-testid="payment-promo-link"
          >
            Have a promo code?
          </button>
        )}
        <p className="tier-fineprint">
          Cancel or change tiers anytime from Settings → Manage subscription.<br />
          Stripe handles billing securely. We never see your card.
        </p>
      </div>
    </div>
  );
}

const PRICE_BY_SLUG = { spark: "1.99", efflora: "4.99", aurora: "9.99", eternal: "19.99" };
const YEARLY_PRICE_BY_SLUG = { spark: "34.90", efflora: "69.90", aurora: "129.90", eternal: "250.00" };

function fmtCap(n, period) {
  if (n === null || n === undefined) return `Unlimited ${period}`;
  return `${n}/${period}`;
}

function tierBullets(t) {
  const c = t.caps || {};
  const f = t.features || {};
  const bullets = [];
  bullets.push(`💬 Unlimited chat`);
  bullets.push(`🖼️ ${fmtCap(c.wallpaper_per_week, "wk")} wallpapers`);
  if (f.voice) bullets.push(`🎙️ ${fmtCap(c.voice_per_day, "day")} voice turns`);
  if (f.vision) bullets.push(`📷 ${fmtCap(c.vision_per_day, "day")} photo reacts`);
  // Phase 30.Q — Themes are free for everyone, no longer a paid-tier perk.
  if (f.daily_checkin) bullets.push(`☀️ Daily check-ins`);
  if (f.social_integration) bullets.push(`🎵 Music & social integration`);
  if (f.milestone_letters) bullets.push(`💌 Milestone letters`);
  if (f.priority_response) bullets.push(`⚡ Priority responses`);
  if (f.early_access) bullets.push(`🆕 Early access to new features`);
  if (f.yearbook) bullets.push(`📓 Aura's Yearbook every Dec 31`);
  if (f.seasonal_packs) bullets.push(`🌸 Monthly exclusive wallpaper packs`);
  if (f.gift_friend) bullets.push(`🎁 Gift Aurora to a friend yearly`);
  // Phase 24 — Eternal-exclusive add-ons
  if (f.voice_recognition) bullets.push(`🛟 Voice Recognition (household)`);
  if (f.continuous_listening) bullets.push(`🎙️ Continuous Listening — "Hey Aura"`);
  if (f.story_book) bullets.push(`📖 Aura's Story Book (annual PDF)`);
  if (f.premium_voice) bullets.push(`✨ HD Voice (ElevenLabs)`);
  return bullets;
}
